import React, {useEffect, useMemo, useRef} from 'react'
import {shouldUpdate} from 'recompose'
import {ObjUtil, TypeUtil} from 'yougile-util'
import {DataObjectId, Report, TaskData} from 'yougile-model'
import {Sync} from '@client/sync'
import {useBoardStore} from '@components/board/board-store'
import {ShowMoreButton} from '@components/columns/show-more-button'
import {FlatLoader} from '@components/common/base/flat-loader/flat-loader'
import {Loader} from '@components/common/base/loader'
import {TaskCardDrag} from '@components/task-card/task-card-drag'
import {useReportTasksIds, useTasksDataFromIds} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {
  QueuedRender,
  taskCardPlaceholderClassName
} from '@containers/queued-render'
import {TimeoutRender} from '@containers/timeout-render'
import {withUpdaters} from '@containers/updaters'
import {BoardFilterUpdater} from '@containers/updaters/board-filter-updater'
import {SyncUpdater} from '@containers/updaters/sync-updater'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {Task} from '@extensions/task'

const PAGE_SIZE = 50

interface Props {
  report: Report
  onGetTasks: (tasks: TaskData[]) => void
  boardId: DataObjectId
}

interface InnerProps {
  tasks: TaskData[]
  boardId: DataObjectId
}

const StatsReportsTaskListBase = ({tasks, boardId}: InnerProps) => {
  const [maxShown, setMaxShown] = React.useState(PAGE_SIZE)
  const showMore = () => setMaxShown(maxShown + PAGE_SIZE)
  const [boardState] = useBoardStore()
  const visibleTasks = tasks.slice(0, maxShown)
  const [showLoader, setShowLoader] = React.useState(!!visibleTasks.length)
  const hideLoader = () => setShowLoader(false)

  const tasksLeft = tasks.length - maxShown

  return (
    <div
      className={cn(
        // do not display when there are no tasks
        'space-y-8',
        // 2px padding plus 6px margin gives 8px as designed
        'mt-6',
        // anti crop paddings; bottom one is shown only if all tasks are shown b/c
        // a button does not overflow
        'pt-2',
        !!tasksLeft && 'pb-2'
      )}
    >
      {visibleTasks.map((t, idx) => {
        const task = Task.getById(t.id)
        if (!task) {
          return null
        }
        return (
          <QueuedRender
            key={t.id}
            queue={boardState.tasksRenderQueue}
            id={boardId}
            placeholderClassName={taskCardPlaceholderClassName}
          >
            <TaskCardDrag
              task={task}
              index={idx}
              createMirrorByDrag={true}
              reportColumn={true}
            />
          </QueuedRender>
        )
      })}
      {showLoader && (
        <TimeoutRender timeout={1000}>
          <div className="text-center">
            <Loader size="xsmall" />
          </div>
        </TimeoutRender>
      )}
      <QueuedRender
        queue={boardState.tasksRenderQueue}
        id={boardId}
        onBeforeRender={hideLoader}
      >
        {tasksLeft > 0 ? (
          <ShowMoreButton count={tasksLeft} onClick={showMore} />
        ) : (
          <></>
        )}
      </QueuedRender>
    </div>
  )
}

export const StatsReportsTaskList: React.ComponentClass<Props> = shouldUpdate(
  (prev, next) => !ObjUtil.deepEqual(prev, next, true)
)(
  withUpdaters(
    SyncUpdater({companyType: 'current', filter: '*', debounceTime: 3000}),
    BoardFilterUpdater()
  )(({onGetTasks, report, boardId}: Props) => {
    const isMounted = useRef(true)
    useEffect(
      () => () => {
        isMounted.current = false
      },
      []
    )

    const {
      data: tasksIds,
      isLoading: areTasksLoading,
      isFetching: areTasksFetching
    } = useReportTasksIds({report, shouldUpdateOnDataChange: true})
    const box = Sync.getCurrentSandbox()

    const filteredTasks = useMemo(
      () =>
        tasksIds
          ? BoardFilters.filter(
              box.getHub(boardId) || PageService.getCurrentBoard(),
              tasksIds.map(id => box.getHub(id)).filter(TypeUtil.isTruthy)
            ).map(t => t.id)
          : [],
      [tasksIds, box, boardId]
    )
    const filteredTasksData = useTasksDataFromIds(filteredTasks)

    useEffect(() => {
      if (isMounted.current) {
        onGetTasks?.(filteredTasksData)
      }
    }, [filteredTasksData, onGetTasks])

    if (areTasksLoading) {
      return (
        <div className="text-center mt-8">
          <Loader size="xsmall" />
        </div>
      )
    }

    return (
      <>
        {areTasksFetching && (
          <div className="relative">
            <div className="absolute top-[-3px] left-0 right-0">
              <FlatLoader />
            </div>
          </div>
        )}
        <StatsReportsTaskListBase tasks={filteredTasksData} boardId={boardId} />
      </>
    )
  })
)
